import type { RoomId } from '../core/GameState';

export type ObjectKind = 'bessie' | 'trough' | 'vat' | 'table' | 'tub' | 'board' | 'desk' | 'terminal' | 'rack' | 'dials' | 'door';

export interface RoomObject {
  id: string;
  kind: ObjectKind;
  label: string;
  x: number;
  y: number;
  w: number;
  h: number;
  /** Visual height in pixels */
  z: number;
  color: number;
  /** Door target */
  target?: { room: RoomId; tile: { x: number; y: number } };
  /** Only drawn and usable once this skill is owned */
  requiresSkill?: string;
}

export interface RoomDef {
  id: RoomId;
  name: string;
  cols: number;
  rows: number;
  floorA: number;
  floorB: number;
  background: string;
  spawn: { x: number; y: number };
  objects: RoomObject[];
}

export const ROOMS: Record<RoomId, RoomDef> = {
  barn: {
    id: 'barn',
    name: 'Barn',
    cols: 9,
    rows: 7,
    floorA: 0xc5a073,
    floorB: 0xbd9568,
    background: '#514139',
    spawn: { x: 4, y: 4 },
    objects: [
      { id: 'bessie', kind: 'bessie', label: 'Bessie', x: 2, y: 1, w: 2, h: 2, z: 70, color: 0xd9c7a8 },
      { id: 'trough', kind: 'trough', label: 'Feed trough', x: 5, y: 1, w: 2, h: 1, z: 26, color: 0x9c6b3c },
      { id: 'hay1', kind: 'trough', label: 'Hay', x: 0, y: 5, w: 1, h: 1, z: 40, color: 0xd8b64a },
      {
        id: 'door_creamery',
        kind: 'door',
        label: 'To Creamery →',
        x: 8,
        y: 3,
        w: 1,
        h: 1,
        z: 0,
        color: 0x4b7bd6,
        target: { room: 'creamery', tile: { x: 1, y: 3 } },
      },
      {
        id: 'door_field',
        kind: 'door',
        label: '↑ To Sunny Pasture',
        x: 4,
        y: 0,
        w: 1,
        h: 1,
        z: 0,
        color: 0x55a85f,
        target: { room: 'field', tile: { x: 4, y: 5 } },
        requiresSkill: 'field',
      },
    ],
  },
  creamery: {
    id: 'creamery',
    name: 'Creamery',
    cols: 9,
    rows: 7,
    floorA: 0xd8e1d9,
    floorB: 0xcbd8d0,
    background: '#43575b',
    spawn: { x: 1, y: 3 },
    objects: [
      { id: 'vat', kind: 'vat', label: 'Cheese vat', x: 3, y: 1, w: 2, h: 2, z: 60, color: 0x9fb3c8 },
      { id: 'table', kind: 'table', label: 'Draining table', x: 6, y: 3, w: 2, h: 1, z: 44, color: 0xc9a46a },
      { id: 'tub', kind: 'tub', label: 'Brine tub', x: 1, y: 5, w: 1, h: 1, z: 46, color: 0x7fa6c4 },
      { id: 'board', kind: 'board', label: 'Market board', x: 7, y: 0, w: 1, h: 1, z: 80, color: 0x6b8e4e },
      { id: 'desk', kind: 'desk', label: 'Supply desk', x: 7, y: 5, w: 1, h: 1, z: 40, color: 0xb07a45 },
      {
        id: 'door_barn',
        kind: 'door',
        label: '← To Barn',
        x: 0,
        y: 3,
        w: 1,
        h: 1,
        z: 0,
        color: 0x4b7bd6,
        target: { room: 'barn', tile: { x: 7, y: 3 } },
      },
      {
        id: 'door_cellar',
        kind: 'door',
        label: '↓ To Cellar',
        x: 4,
        y: 6,
        w: 1,
        h: 1,
        z: 0,
        color: 0x6b4bd6,
        target: { room: 'cellar', tile: { x: 4, y: 5 } },
        requiresSkill: 'cellar',
      },
    ],
  },
  cellar: {
    id: 'cellar',
    name: 'Aging Cellar',
    cols: 9,
    rows: 7,
    floorA: 0x858092,
    floorB: 0x797586,
    background: '#383443',
    spawn: { x: 4, y: 5 },
    objects: [
      { id: 'rack_west', kind: 'rack', label: 'Aging racks', x: 1, y: 1, w: 1, h: 3, z: 90, color: 0x8b6b45 },
      { id: 'rack_east', kind: 'rack', label: 'Aging racks', x: 7, y: 1, w: 1, h: 3, z: 90, color: 0x8b6b45 },
      { id: 'dials', kind: 'dials', label: 'Climate dials', x: 4, y: 0, w: 1, h: 1, z: 56, color: 0x5f7f8f },
      {
        id: 'door_creamery_up',
        kind: 'door',
        label: '↑ To Creamery',
        x: 4,
        y: 6,
        w: 1,
        h: 1,
        z: 0,
        color: 0x4b7bd6,
        target: { room: 'creamery', tile: { x: 4, y: 5 } },
      },
    ],
  },
  field: {
    id: 'field',
    name: 'Sunny Pasture & Crashed Spaceship',
    cols: 9,
    rows: 7,
    floorA: 0x9caf7b,
    floorB: 0x91a46f,
    background: '#465b45',
    spawn: { x: 4, y: 5 },
    objects: [
      { id: 'pasture', kind: 'bessie', label: "Bessie's grazing area", x: 1, y: 1, w: 3, h: 2, z: 30, color: 0x87c768 },
      { id: 'spaceship', kind: 'desk', label: 'Crashed spaceship tech lab', x: 6, y: 1, w: 2, h: 2, z: 80, color: 0x5f7f8f },
      { id: 'field_terminal', kind: 'terminal', label: 'Alien tech terminal', x: 6, y: 4, w: 1, h: 1, z: 45, color: 0x6eadd4 },
      {
        id: 'door_barn_field',
        kind: 'door',
        label: '↓ To Barn',
        x: 4,
        y: 6,
        w: 1,
        h: 1,
        z: 0,
        color: 0x4b7bd6,
        target: { room: 'barn', tile: { x: 4, y: 1 } },
      },
    ],
  },
};
